import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { endOfDay, format } from 'date-fns'
import { useFormik } from 'formik'
import { get, has, isEmpty, isEqual, isNil, omit } from 'lodash-es'
import * as Yup from 'yup'

import {
  EMPTY_LIST,
  EMPTY_STRING,
  NOTIFICATION_TYPE,
} from '../../../constants/common'
import {
  DATE_API_DATA_FORMAT,
  DATE_SHORT_FORMAT,
} from '../../../constants/date'
import {
  CUSTOM_FILTER_TYPE,
  DATE_FILTER_TYPE,
  DATE_HIDDEN_TIME_FILTER_TYPE,
  MULTISELECT_FILTER_TYPE,
  NUMBER_RANGE_FILTER_TYPE,
  SELECT_FILTER_TYPE,
  TOGGLE_FILTER_TYPE,
} from '../../../constants/filter'
import { PAGINATION_ALL_ITEMS_SIZE } from '../../../constants/pagination'
import { SIZE } from '../../../constants/size'
import { FORMIK_EMPTY_VALUE } from '../../../constants/validation'
import {
  useInlineNotifications,
  useNotification,
  useScreen,
  useUkTimeZone,
} from '../../../hooks'
import { getNormalizedParams, getSingleFieldParams } from '../../../utils/api'
import { preventDefault, stopPropagation } from '../../../utils/events'
import {
  getDateEndParam,
  getDateOptions,
  getDateRangeParam,
  getDateStartParam,
  getRelatedFilters,
  getSelectTypeOptions,
  getValuesFromOptions,
  getVisibleFilters,
} from '../../../utils/filter'
import { ConfirmationModal } from '../../Confirmation'
import { CustomSelect } from '../../CustomSelect'
import {
  FormInlineNotifications,
  useFormValidationErrorsNotification,
} from '../../Form'
import { InputField } from '../../Input'
import Modal, { Box, Content, Header } from '../../Modal'
import { NotificationInline } from '../../Notification'
import { Switch } from '../../Switch'
import Text from '../../Text'
import {
  deleteFilterTemplate,
  getFilterSingleFieldData,
  getFilterTemplates,
  updateFilterTemplate,
} from '../Filter.api'
import {
  FILTER_TEMPLATE_NAME_FIELD,
  FILTER_TEMPLATE_NAME_MAX_LENGTH,
} from '../Filter.constants'
import {
  getFilterSingleFieldUrlParams,
  getStaticFilterOptionsByName,
  getStaticFilterSingleFieldResponse,
} from '../Filter.utils'
import { FilterDateRangeModal } from '../FilterDateRangeModal'
import { FilterModalActions } from '../FilterModalActions'
import { FilterModalAvailableFilters } from '../FilterModalAvailableFilters'
import { FilterModalReplacement } from '../FilterModalReplacement'
import { FilterSaveModal } from '../FilterSaveModal'

import { useFilterModalValidation } from './FilterModal.hooks'
import {
  getNextRangeDateOptions,
  getOptionLabel,
  getOptionValue,
  isEqualApplyFilters,
  isEqualEditTemplateFilter,
  isSizeLarge,
} from './FilterModal.utils'

import * as styles from './FilterModal.module.css'

function FilterModal({
  apiUrl,
  availableFilters,
  canViewFilterTemplate,
  entity,
  excludeFilters,
  filterValues,
  initialRangeFilterNames,
  isEditing: initialEditing,
  parsePermanentFilters,
  permanentFilters,
  responsive,
  setFilterValues,
  singleFieldApiParams,
  value: initialValue,
  onClose,
}) {
  const { t } = useTranslation()

  const { timezone } = useUkTimeZone()

  const { handleAddNotification } = useNotification()
  const {
    handleAddNotification: handleAddInlineNotification,
    handleRemoveNotification: handleRemoveInlineNotification,
    notifications,
  } = useInlineNotifications()

  const { isValidForm, setValidationErrors } = useFilterModalValidation({
    onRemoveNotification: handleRemoveInlineNotification,
    onSetNotification: handleAddInlineNotification,
  })

  const { isMobileScreenSize } = useScreen() // TODO make same by css media instead of useScreen

  // Hidden-type filters must not push the modal to LARGE while the Accordion
  // (counting rendered items) stays single-column.
  const visibleFiltersCount = getVisibleFilters(availableFilters).length

  const modalSize = visibleFiltersCount > 6 ? SIZE.LARGE : SIZE.MEDIUM

  const shouldAdaptToMobileScreen =
    responsive && isMobileScreenSize && isSizeLarge(modalSize)

  const [filterOptions, setFilterOptions] = useState(null)
  const [editingOptions, setEditingOptions] = useState(null)

  const [isSaving, setSaving] = useState(false)
  const [isReplacement, setReplacement] = useState(false)
  const [isEditing, setEditing] = useState(initialEditing)

  const [value, setValue] = useState(initialValue)
  const [errorText, setErrorText] = useState(EMPTY_STRING)

  const [isClearConfirm, setClearConfirm] = useState(false)
  const [isDeleteConfirm, setDeleteConfirm] = useState(false)
  const [isCloseConfirm, setCloseConfirm] = useState(false)

  const [isLoading, setLoading] = useState(false)
  const [isPublic, setPublic] = useState(false)

  const [isEmptyFilters, setEmptyFilters] = useState(false)

  const [dateRangeName, setDateRangeName] = useState(false)

  const formik = useFormik({
    initialValues: {
      [FILTER_TEMPLATE_NAME_FIELD]: FORMIK_EMPTY_VALUE,
    },
    validateOnChange: false,
    validationSchema: Yup.object({
      [FILTER_TEMPLATE_NAME_FIELD]: Yup.string()
        .required(true)
        .max(
          FILTER_TEMPLATE_NAME_MAX_LENGTH,
          t('Error.MaxLengthKey', { length: FILTER_TEMPLATE_NAME_MAX_LENGTH })
        ),
    }),
    onSubmit: handleEditTemplate,
  })

  useFormValidationErrorsNotification({
    formikContext: formik,
    onRemoveNotification: handleRemoveInlineNotification,
    onSetNotification: handleAddInlineNotification,
  })

  const [prevEmptyFiltersTrigger, setPrevEmptyFiltersTrigger] = useState({
    editingOptions,
    isEmptyFilters,
  })

  // Resets the empty-filters flag once filters are present again, during render
  if (
    editingOptions !== prevEmptyFiltersTrigger.editingOptions ||
    isEmptyFilters !== prevEmptyFiltersTrigger.isEmptyFilters
  ) {
    setPrevEmptyFiltersTrigger({ editingOptions, isEmptyFilters })

    if (isEmptyFilters && !isEmpty(getValuesFromOptions(editingOptions))) {
      setEmptyFilters(false)
    }
  }

  useEffect(() => {
    if (filterValues) {
      const currentFilters = omit(filterValues, excludeFilters)

      if (!isEmpty(currentFilters)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- updates filter state in response to filter prop changes
        setLoading(true)

        async function getFilterData(item) {
          // A facet with static options answers locally — nothing to fetch.
          // TODO: drop this branch once every facet is served by the endpoint.
          const staticOptions =
            getStaticFilterOptionsByName(availableFilters)[item[0]]

          if (staticOptions) {
            return getStaticFilterSingleFieldResponse(staticOptions, item[1])
          }

          const params = getFilterSingleFieldUrlParams({
            field_name: item[0],
            items: item[1],
            permanentFilters: parsePermanentFilters(permanentFilters),
            size: PAGINATION_ALL_ITEMS_SIZE,
          })

          return getFilterSingleFieldData({ apiUrl, entity, params })
        }

        const multiSelectOptions = getSingleFieldParams(currentFilters)
        const selectOptions = getSelectTypeOptions(
          currentFilters,
          availableFilters
        )
        const dateOptions = getDateOptions({
          availableFilters,
          currentFilters,
          timezone,
        })
        const params = [
          ...getNormalizedParams(selectOptions),
          ...multiSelectOptions,
        ]

        Promise.all(params.map(getFilterData)).then(response => {
          setFilterOptions(
            params.reduce(
              (acc, item, index) => {
                acc[item[0]] = !isNil(selectOptions[item[0]])
                  ? response[index].data.items[0]
                  : response[index].data.items

                return acc
              },
              { ...currentFilters, ...dateOptions }
            )
          )

          setLoading(false)
        })
      }
    }
  }, [availableFilters, entity, filterValues])

  useEffect(() => {
    if (isEditing && value) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- updates filter state in response to filter prop changes
      setEditingOptions(filterOptions)

      formik.setFieldValue(FILTER_TEMPLATE_NAME_FIELD, value.name)

      setPublic(value.is_public)
    }
  }, [filterOptions, isEditing])

  useEffect(() => {
    if (value && !isEmpty(value)) {
      getFilterTemplates(null, value.id).then(data => {
        const currentFilters = omit(data.data.filter_values, excludeFilters)

        async function getFilterData(item) {
          const params = getFilterSingleFieldUrlParams({
            field_name: item[0],
            items: item[1],
            permanentFilters: parsePermanentFilters(permanentFilters),
            size: PAGINATION_ALL_ITEMS_SIZE,
          })

          return getFilterSingleFieldData({ apiUrl, entity, params })
        }

        const params = getSingleFieldParams(currentFilters)

        const dateOptions = getDateOptions({
          availableFilters,
          currentFilters,
          timezone,
        })

        Promise.all(params.map(getFilterData)).then(response => {
          setFilterOptions(
            params.reduce(
              (acc, item, index) => {
                acc[item[0]] = response[index].data.items

                return acc
              },
              { ...currentFilters, ...dateOptions }
            )
          )
        })
      })
    }
  }, [value])

  function getOptions() {
    let options = filterOptions
    let handler = setFilterOptions

    if (isEditing) {
      options = editingOptions
      handler = setEditingOptions
    }

    return [options, handler]
  }

  const [options] = getOptions()

  const currentFilters = isEditing
    ? getValuesFromOptions(filterOptions)
    : omit(filterValues, excludeFilters)

  const nextFilters = getValuesFromOptions(options)

  function checkAreEqualFilters() {
    const [options] = getOptions()

    const currentFilters = isEditing
      ? getValuesFromOptions(filterOptions)
      : omit(filterValues, excludeFilters)

    const nextFilters = getValuesFromOptions(options)

    return isEqual(nextFilters, currentFilters)
  }

  function handleCloseFilters() {
    if (isSaving) {
      setSaving(false)
      setCloseConfirm(false)

      return
    }

    if (isEditing) {
      if (initialEditing) {
        onClose()
      } else {
        setEditing(false)
        setCloseConfirm(false)
      }

      return
    }

    onClose()
  }

  function handleApply(event) {
    stopPropagation(event)
    preventDefault(event)

    formik.setFormikState(prev => ({
      ...prev,
      submitCount: prev.submitCount + 1,
    }))

    if (
      !isValidForm({
        innerValidationSchemaProps: {
          isEqual: isEqualApplyFilters,
          isEqualProps: { currentFilters, nextFilters },
        },
      })
    ) {
      return
    }

    const params = getValuesFromOptions(filterOptions)

    setFilterValues({ ...params, ...permanentFilters })

    handleAddNotification({
      title: t('Notification.CommonTitleAppliedKey'),
      type: NOTIFICATION_TYPE.success,
    })

    handleCloseFilters()
  }

  function handleClearFilter(filterKey) {
    const [options, handler] = getOptions()

    if (filterKey) {
      const nextOptions = omit(options, filterKey)

      if (isEmpty(nextOptions)) {
        handler(null)
      } else {
        handler(nextOptions)
      }
    } else {
      handler(null)

      setClearConfirm(false)
    }
  }

  function handleSetRangeDate(
    startDateKey,
    startDateValue,
    endDateKey,
    endDateValue
  ) {
    const [options, handler] = getOptions()

    handler(options)

    let nextOptions = getNextRangeDateOptions({
      asEnd: false,
      date: startDateValue,
      key: startDateKey,
      options,
      timezone,
    })

    nextOptions = getNextRangeDateOptions({
      asEnd: true,
      date: endDateValue,
      key: endDateKey,
      options: nextOptions,
      timezone,
    })
    handler(isEmpty(nextOptions) ? null : nextOptions)
  }

  function handleToggle(key, value) {
    const [options, handler] = getOptions()

    if (value) {
      handler({ ...options, [key]: value })
    } else {
      handler(omit(options, [key]))
    }
  }

  function handleDate(params, value) {
    const [options, handler] = getOptions()

    const startParam = params[0]
    const endParam = params[1]

    if (get(options, [startParam, 'value']) === value || !value) {
      const nextOptions = omit(options, [startParam, endParam])

      handler(isEmpty(nextOptions) ? null : nextOptions)

      return
    }

    const startDateValue = format(value, DATE_API_DATA_FORMAT)

    const endDateValue = format(endOfDay(value), DATE_API_DATA_FORMAT)

    const relatedFilters = getRelatedFilters(availableFilters)

    let nextOptions = options

    const dateRangeParam = getDateRangeParam(startParam)

    if (has(relatedFilters, dateRangeParam)) {
      nextOptions = omit(nextOptions, dateRangeParam)
    }

    handler({
      ...nextOptions,
      [endParam]: {
        key: endParam,
        label: format(value, DATE_SHORT_FORMAT),
        value: endDateValue,
      },
      [startParam]: {
        key: startParam,
        label: format(value, DATE_SHORT_FORMAT),
        value: startDateValue,
      },
    })
  }

  function handleSelect(key, value) {
    const [options, handler] = getOptions()

    if (!options) {
      handler({ [key]: value })

      return
    }

    handler({ ...options, [key]: value })
  }

  function handleSelectMulti(key, value) {
    const [options, handler] = getOptions()

    if (!options) {
      handler({ [key]: [value] })

      return
    }

    if (!options[key]) {
      let nextOptions = options

      const relatedFilters = getRelatedFilters(availableFilters)

      const startDateParam = getDateStartParam(key)
      const endDateParam = getDateEndParam(key)

      if (has(relatedFilters, key)) {
        nextOptions = omit(nextOptions, [startDateParam, endDateParam])
      }

      handler({ ...nextOptions, [key]: [value] })

      return
    }

    if (options[key].map(i => i.value).includes(value.value)) {
      const nextOptions = options[key].filter(i => value.value !== i.value)

      if (nextOptions.length > 0) {
        handler({
          ...options,
          [key]: nextOptions,
        })
      } else {
        handler(omit(options, [key]))
      }
    } else {
      handler({
        ...options,
        [key]: options[key].concat(value),
      })
    }
  }

  function handleSetId(key, value, type) {
    let handler

    switch (type) {
      case SELECT_FILTER_TYPE:
        handler = handleSelect

        break
      // A number-range bound is one param with a plain value — the toggle's
      // set-or-omit shape.
      case NUMBER_RANGE_FILTER_TYPE:
      case TOGGLE_FILTER_TYPE:
        handler = handleToggle

        break
      case DATE_FILTER_TYPE:
      case DATE_HIDDEN_TIME_FILTER_TYPE:
        handler = handleDate

        break
      case MULTISELECT_FILTER_TYPE:
      case CUSTOM_FILTER_TYPE:
        handler = handleSelectMulti

        break
    }

    if (handler) {
      handler(key, value, type)
    }
  }

  function handleSaveTemplate() {
    setSaving(true)
  }

  function handleReplaceTemplate() {
    setReplacement(true)
  }

  function getTemplates(search, apiParams) {
    const params = {
      ...(search ? { search } : null),
      table_name: entity.plural,
    }

    return getFilterTemplates({
      ...apiParams,
      ...params,
    })
  }

  function handleCloseEdit() {
    if (initialEditing) {
      onClose()
    } else {
      setEditing(false)
    }
  }

  function handleEditTemplate({ name }, { setSubmitting }) {
    const prevFilterValues = getValuesFromOptions(filterOptions)
    const nextFilterValues = getValuesFromOptions(editingOptions)

    if (isEmpty(nextFilterValues)) {
      setEmptyFilters(true)

      return
    }

    if (
      !isValidForm({
        innerValidationSchemaProps: {
          isEqual: isEqualEditTemplateFilter,
          isEqualProps: {
            isPublic,
            name,
            nextFilterValues,
            prevFilterValues,
            value,
          },
        },
      })
    ) {
      setSubmitting(false)

      return
    }

    setLoading(true)

    updateFilterTemplate(
      {
        filter_values: nextFilterValues,
        is_public: isPublic,
        name,
      },
      value.id
    )
      .then(({ data }) => {
        handleAddNotification({
          title: t('Notification.CommonTitleEditedKey'),
          type: NOTIFICATION_TYPE.success,
        })

        // Apply saved template values to current filter values
        setFilterValues({ ...data.filter_values, ...permanentFilters })

        handleCloseEdit()
      })
      .catch(error => setErrorText(error.response.data.detail))
      .finally(() => setLoading(false))
  }

  function handleCloseClear(event) {
    stopPropagation(event)

    setClearConfirm(false)
  }

  function handleOpenClear(event) {
    stopPropagation(event)

    setClearConfirm(true)
  }

  function handleOpenDelete(event) {
    stopPropagation(event)

    setDeleteConfirm(true)
  }

  function handleCloseConfirm(event) {
    stopPropagation(event)

    if (checkAreEqualFilters() && !!get(value, 'is_public') === isPublic) {
      if (isEditing && !initialEditing) {
        setEditing(false)
      } else {
        onClose()
      }

      setValidationErrors(EMPTY_LIST)

      return
    }

    setCloseConfirm(true)
  }

  function handleClearAllFilters() {
    handleClearFilter()
  }

  function handleCancelDelete(event) {
    stopPropagation(event)

    setDeleteConfirm(false)
  }

  function handleCancelClose(event) {
    stopPropagation(event)

    setCloseConfirm(false)
  }

  function handleDeleteTemplate() {
    deleteFilterTemplate(get(value, 'id')).then(() => {
      setDeleteConfirm(false)

      if (initialEditing) {
        onClose()
      } else {
        setEditing(false)
      }

      handleAddNotification({
        title: t('Notification.CommonTitleDeletedKey'),
        type: NOTIFICATION_TYPE.success,
      })
    })
  }

  function handleTogglePublic(checked) {
    setPublic(checked)
  }

  function handleEdit(option) {
    setValue(option)
    setEditing(true)
    setValidationErrors(EMPTY_LIST)
  }

  function handleCloseSaving() {
    setSaving(false)
  }

  function handleCloseReplacement() {
    setReplacement(false)
  }

  function handleClearValue() {
    formik.setFieldValue(FILTER_TEMPLATE_NAME_FIELD, FORMIK_EMPTY_VALUE)
  }

  function handleOpenDateRange(name) {
    setDateRangeName(name)
  }

  function handleCloseDateRange() {
    setDateRangeName(null)
  }

  return (
    <>
      {!isDeleteConfirm &&
        !isClearConfirm &&
        !isCloseConfirm &&
        !isSaving &&
        !isReplacement && (
          <Modal onClose={handleCloseConfirm}>
            <form onSubmit={formik.handleSubmit}>
              <Box isFullScreen={shouldAdaptToMobileScreen} size={modalSize}>
                <Header
                  isFullScreen={shouldAdaptToMobileScreen}
                  text={t(
                    isEditing
                      ? 'Modal.EditFilterTemplateTitleKey'
                      : 'Modal.EditFilterTitleKey'
                  )}
                  onClose={handleCloseConfirm}
                />
                <Content isFullScreen={shouldAdaptToMobileScreen}>
                  <FormInlineNotifications
                    formikContext={formik}
                    notifications={notifications}
                  />

                  {isEditing ? (
                    <>
                      {isEmptyFilters && (
                        <NotificationInline
                          className={styles.notification}
                          text={t('Notification.EmptyFilterErrorTitleKey')}
                          type='error'
                        />
                      )}
                      <InputField
                        hasCounter
                        inline
                        disabled={isLoading}
                        errorText={formik.errors.name || errorText.name}
                        height={40}
                        maxLength={255}
                        name='name'
                        placeholder={t('Filter.TemplateNamePlaceholderKey')}
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onClear={handleClearValue}
                      />
                    </>
                  ) : (
                    <>
                      {canViewFilterTemplate && (
                        <CustomSelect
                          activeItems={
                            value ? [getOptionValue(value)] : EMPTY_LIST
                          }
                          api={getTemplates}
                          buttonClassName={styles.button}
                          buttonHeight={40}
                          className={styles.select}
                          disabled={isLoading}
                          getLabel={getOptionLabel}
                          getValue={getOptionValue}
                          inputHeight={40}
                          listType='filter'
                          placeholder={t('Table.FilterTemplateBtnKey')}
                          searchKey='search'
                          onClick={setValue}
                          onEdit={handleEdit}
                        />
                      )}
                    </>
                  )}
                  {isEditing && (
                    <div className={styles.filter}>
                      <Text
                        isSecondary
                        disabled={isLoading}
                      >{`${t('Modal.MakeTeplatePublicLabelKey')}:`}</Text>
                      <Switch
                        className={styles.switch}
                        disabled={isLoading}
                        value={isPublic}
                        onChange={handleTogglePublic}
                      />
                    </div>
                  )}
                  <FilterModalAvailableFilters
                    apiUrl={apiUrl}
                    availableFilters={availableFilters}
                    entity={entity}
                    filterOptions={getOptions()[0]}
                    initialRangeFilterNames={initialRangeFilterNames}
                    isLoading={isLoading}
                    permanentFilters={permanentFilters}
                    shouldAdaptToMobileScreen={shouldAdaptToMobileScreen}
                    singleFieldApiParams={singleFieldApiParams}
                    onClear={handleClearFilter}
                    onClick={handleSetId}
                    onOpenDateRange={handleOpenDateRange}
                    onSetDateRange={handleSetRangeDate}
                  />
                </Content>
                <FilterModalActions
                  canViewFilterTemplate={canViewFilterTemplate}
                  editingOptions={editingOptions}
                  filterOptions={filterOptions}
                  isEditing={isEditing}
                  isLoading={isLoading}
                  onCancel={handleCloseConfirm}
                  onClear={handleOpenClear}
                  onDelete={handleOpenDelete}
                  onReplaceTemplate={handleReplaceTemplate}
                  onSaveTemplate={handleSaveTemplate}
                  onSubmit={isEditing ? null : handleApply}
                />
              </Box>
            </form>
          </Modal>
        )}
      {isClearConfirm && !isDeleteConfirm && !isCloseConfirm && (
        <ConfirmationModal
          actionButtonsProps={{
            submitText: t('Modal.ClearFilterConfirmBtnKey'),
          }}
          description={t('Modal.ClearFilterConfirmTextKey')}
          onCancel={handleCloseClear}
          onSubmit={handleClearAllFilters}
        />
      )}
      {!isClearConfirm && isDeleteConfirm && !isCloseConfirm && (
        <ConfirmationModal
          actionButtonsProps={{
            cancelText: t('Modal.DeleteFilterCancelBtnKey'),
            submitText: t('Modal.DeleteFilterConfirmBtnKey'),
          }}
          description={t('Modal.DeleteFilterConfirmTextKey')}
          title={t('Modal.ConfirmationDeleteTitleKey')}
          onCancel={handleCancelDelete}
          onSubmit={handleDeleteTemplate}
        />
      )}
      {!isClearConfirm && !isDeleteConfirm && isCloseConfirm && (
        <ConfirmationModal
          actionButtonsProps={{
            cancelText: t('Modal.CommonGoBackBtnKey'),
            submitText: t('Modal.CancelFilterConfirmBtnKey'),
          }}
          description={t('Modal.CancelFilterConfirmTextKey')}
          onCancel={handleCancelClose}
          onSubmit={handleCloseFilters}
        />
      )}
      {isReplacement && (
        <FilterModalReplacement
          entity={entity}
          filterOptions={filterOptions}
          onClose={handleCloseReplacement}
        />
      )}
      {isSaving && (
        <FilterSaveModal
          entity={entity}
          filterOptions={filterOptions}
          onClose={handleCloseSaving}
        />
      )}
      {dateRangeName && (
        <FilterDateRangeModal
          availableFilters={availableFilters}
          dateRangeName={dateRangeName}
          filterOptions={filterOptions}
          setDateRangeName={setDateRangeName}
          onClear={handleClearFilter}
          onClose={handleCloseDateRange}
          onSetDate={handleSetId}
          onSetDateRange={handleSetRangeDate}
        />
      )}
    </>
  )
}

export default FilterModal
